import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema } from 'src/app/services/models/datasets.models';
import { Subject, takeUntil, forkJoin, debounceTime, switchMap, EMPTY } from 'rxjs';

interface SelectOption {
  value: any;
  label: string;
}

@Component({
  selector: 'bhub-radial-button-cell',
  standalone: false,
  templateUrl: './radial-button-cell.component.html',
  styleUrl: './radial-button-cell.component.scss'
})
export class RadialButtonCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  // UI Properties
  labelText: string = '';
  selectedValue: any = '';
  radialOptions: SelectOption[] = [];
  placeholder: string = '';

  // Configuration properties
  dataSource: 'context' | 'dataset' | 'none' = 'none';
  contextObjectKey: string = '';
  fieldName: string = '';
  datasetId: number | null = null;
  referenceDatasetId: number | null = null;

  // Layout configuration
  layoutDirection: 'horizontal' | 'vertical' | 'auto' = 'auto';
  columnsCount: number = 0; // 0 means auto

  // State
  isLoadingOptions = false;
  private destroy$ = new Subject<void>();

  // Debounced dataset loading to prevent duplicate requests
  private loadDatasetOptions$ = new Subject<{datasetId: number, displayField: string, valueField: string}>();
  private currentOptionsKey: string = '';

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService
  ) {
    super();

    // Set up debounced dataset options loading to prevent duplicate requests
    this.loadDatasetOptions$
      .pipe(
        debounceTime(100), // Wait 100ms between requests
        switchMap(({ datasetId, displayField, valueField }) => {
          const optionsKey = `${datasetId}-${displayField}-${valueField}`;
          if (optionsKey === this.currentOptionsKey) {
            // Don't reload if it's the same configuration
            return EMPTY;
          }
          this.currentOptionsKey = optionsKey;
          this.isLoadingOptions = true;


          // Load both dataset schema and actual data
          const filters = this.cellFilterService.buildCellFilters(this.cell?.dataBinding?.cellFilters, this.viewManager);
          return forkJoin({
            datasets: this.datasetService.getDatasetReferences(),
            data: this.dynamicTableService.selectEntries(datasetId, -1, undefined, filters, this.getConfigValue('distinct', false) as boolean) // Load all entries with filters
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          const datasetId = this.extractDatasetIdFromKey(this.currentOptionsKey);
          const { displayField, valueField } = this.extractFieldsFromKey(this.currentOptionsKey);

          const dataset = result.datasets.get(datasetId);
          if (dataset && result.data.rowData) {
            // Convert actual data to radial button options
            this.radialOptions = this.convertDataToOptions(result.data.rowData, displayField, valueField);
          } else {
            this.radialOptions = [];
          }
          this.isLoadingOptions = false;
        },
        error: (error) => {
          console.error('Error loading dataset options for radial buttons (debounced):', error);
          this.radialOptions = [];
          this.isLoadingOptions = false;
          this.currentOptionsKey = '';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractDatasetIdFromKey(key: string): number {
    const parts = key.split('-');
    return parseInt(parts[0], 10);
  }

  private extractFieldsFromKey(key: string): { displayField: string, valueField: string } {
    const parts = key.split('-');
    return {
      displayField: parts[1],
      valueField: parts[2]
    };
  }

  protected initializeCell(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadRadialOptions();
    this.loadInitialValue();
  }

  private initializeContext(): void {
    if (!this.viewManager) return;

    // Initialize context data if needed
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    // Initialize context for radial button cells that manage their own context
    if (this.dataSource === 'context' && this.contextObjectKey) {
      // Initialize the context key if it doesn't exist
      if (!this.viewManager.contextData.hasOwnProperty(this.contextObjectKey)) {
        // Initialize with empty object that can hold field values
        this.viewManager.contextData[this.contextObjectKey] = {};
      }
    }

    // Initialize context variable for independent fields
    if (this.dataSource === 'none') {
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable) {
        // Store current selected value in the context variable
        this.viewManager.contextData[contextVariable] = this.selectedValue || '';
      }
    }
  }

  protected onCellOrDataChange(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadRadialOptions();
    this.loadInitialValue();
  }

  protected onCellUpdate(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadRadialOptions();
    this.loadInitialValue();
  }

  protected onDataSet(data: any): void {
    // Update value when external data changes
    this.loadInitialValue();
  }

  private updateFieldConfiguration(): void {
    if (!this.cell) return;

    // Get basic configuration
    this.labelText = this.getConfigValue('label', '');
    this.placeholder = this.getConfigValue('placeholder', '');

    // Get layout configuration
    this.layoutDirection = this.getConfigValue('layoutDirection', 'auto') as 'horizontal' | 'vertical' | 'auto';
    this.columnsCount = this.getConfigValue('columnsCount', 0);

    // Determine data source
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'context' | 'dataset' | 'none';

    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;
  }

  private loadInitialValue(): void {
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          this.selectedValue = dataObject[0][this.fieldName] || '';
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          this.selectedValue = dataObject[this.fieldName] || '';
        }
      }
    } else if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      // For dataset source, we might want to load data from the dataset
      this.selectedValue = '';
    } else {
      // Independent field - no initial selection, user will choose from options
      this.selectedValue = '';
    }
  }

  private loadRadialOptions(): void {
    // Check the explicit input type configuration (only static list or dataset field for radial buttons)
    const inputType = this.getConfigValue('inputType', 'static') as 'static' | 'dataset';


    if (inputType === 'static') {
      // Load static options
      const manualOptions = this.getConfigValue('options', null);
      if (manualOptions && Array.isArray(manualOptions) && manualOptions.length > 0) {
        this.radialOptions = manualOptions.map(opt => ({
          value: opt.value !== undefined ? opt.value : opt,
          label: opt.label || String(opt.value !== undefined ? opt.value : opt)
        }));
      } else {
        this.radialOptions = [];
      }
      this.isLoadingOptions = false;
      return;
    } else if (inputType === 'dataset') {
      // Load dataset-based options using debounced approach
      const optionsDatasetId = this.getConfigValue('optionsDatasetId', null);
      const optionsDisplayField = this.getConfigValue('optionsDisplayField', '');
      const optionsValueField = this.getConfigValue('optionsValueField', '');

      if (optionsDatasetId && optionsDisplayField && optionsValueField) {
        // Emit to the debounced Subject instead of calling directly
        this.loadDatasetOptions$.next({
          datasetId: optionsDatasetId,
          displayField: optionsDisplayField,
          valueField: optionsValueField
        });
      } else {
        this.radialOptions = [];
        this.isLoadingOptions = false;
      }
      return;
    }

    // Default to empty options if no valid configuration
    this.radialOptions = [];
    this.isLoadingOptions = false;
  }

  onRadialChange(value: any): void {
    this.selectedValue = value;

    // Update the view manager context if configured
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];

      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          dataObject[0][this.fieldName] = this.selectedValue;
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          dataObject[this.fieldName] = this.selectedValue;
        }
        // Note: We don't emit contextDataChange here because:
        // 1. The context object is already updated in-place
        // 2. All cells share the same viewManager reference
        // 3. Emitting would trigger unnecessary refreshes/API calls
        // The Action Button will read the updated context when saving
      }
    } else if (this.dataSource === 'dataset') {
      // For dataset source, no emit needed - data will be saved by Action Button
    } else {
      // For independent fields, update context variable if configured
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable && this.viewManager) {
        if (!this.viewManager.contextData) {
          this.viewManager.contextData = {};
        }
        this.viewManager.contextData[contextVariable] = this.selectedValue;
      }
      // Note: No emit needed - context is updated in-place
    }
  }

  private convertDataToOptions(rowData: any[], displayField: string, valueField: string): SelectOption[] {
    if (!rowData || rowData.length === 0) {
      return [];
    }

    return rowData.map(row => ({
      value: row[valueField],
      label: String(row[displayField] || row[valueField] || 'Unknown')
    })).filter(option => option.value != null); // Filter out null/undefined values
  }

  // Determine if layout should be in grid mode based on cell dimensions and options count
  get useGridLayout(): boolean {
    if (this.layoutDirection === 'horizontal') return false;
    if (this.layoutDirection === 'vertical') return false;

    // Auto layout: use grid if we have more than 4 options
    return this.radialOptions.length > 4;
  }

  // Calculate grid columns based on cell width and options count
  get gridColumns(): number {
    if (this.columnsCount > 0) return this.columnsCount;

    // Auto calculate columns based on options count
    const optionsCount = this.radialOptions.length;
    if (optionsCount <= 3) return optionsCount;
    if (optionsCount <= 6) return 3;
    if (optionsCount <= 12) return 4;
    return 5;
  }
}
